import {
  Link,
  data as responseData,
  useActionData,
  useNavigation,
} from "react-router";
import { namedAction } from "remix-utils/named-action";
import { z } from "zod";

import {
  isAuthenticatedUser,
  resetSessionWithOldData,
} from "~/.server/sessions.server";
import { validationError } from "~/components/forms/validationErrorResponse.server";
import { assertIsPost, safeRedirect } from "~/lib/utils-server/http.server";
import { getPageMeta } from "~/seo/servers/seo.server";

import { PersonIcon } from "@radix-ui/react-icons";
import { withZod } from "@rvf/zod";

import {
  CheckboxField,
  Form,
  GlobalErrors,
  InputField,
  PasswordField,
} from "~/components/forms/Form";
import { FormErrors } from "~/components/forms/FormErrors";
import { Label } from "~/components/ui/label";
import { H1 } from "~/components/ui/text";
import { TriButton } from "~/components/ui/tri-button";
import { useURL } from "~/contexts";

export const registerSchema = z
  .object({
    firstName: z.string().min(1).max(66),
    lastName: z.string().min(1).max(66),
    email: z.string().email().min(6).max(66),
    password: z.string().min(8).max(66),
    subscribeMail: z.optional(z.string().transform(value => value === "on")),
  })
  .superRefine(({ password }, ctx) => {
    const exactMatch = new RegExp("^(?=.*[A-Za-z])(?=.*d)[A-Za-zd]{8,}$");
    if (password.match(exactMatch)) {
      ctx.addIssue({
        code: "custom",
        message: "The password did not match the requirement",
      });
    }
  });

export const registerValidator = withZod(registerSchema);
export const loader = async ({ request, context }: LoaderArgs) => {
  if (isAuthenticatedUser()) {
    const response = safeRedirect("/account");
    return responseData({}, response);
  }
  // not authenticated, so we can show the register page

  const seo = getPageMeta({
    type: "register",
    options: {
      title: "Register",
      description: "The Register page of Spark* store",
    },
    robots: {
      noFollow: false,
      noIndex: true,
    },
  });

  return { seo };
};

export const action = async ({ request, context }: ActionArgs) => {
  const formData = await request.formData();
  return namedAction(formData, {
    async registerUser() {
      assertIsPost(request);
      const validation = await registerValidator.validate(formData);

      const { api } = context;
      if (validation.error)
        return validationError(validation.error, validation.submittedData);

      try {
        await api.shopperCustomers.registerCustomer({
          body: {
            customer: {
              firstName: validation.data.firstName,
              lastName: validation.data.lastName,
              email: validation.data.email,
              c_subscribeMail: validation.data.subscribeMail,
              login: validation.data.email,
            },
            password: validation.data.password,
          },
        });
        await api.auth.login({
          password: validation.data.password,
          username: validation.data.email,
        });
        const response = safeRedirect("/account", {
          headers: {
            "Set-Cookie": await resetSessionWithOldData(),
          },
        });
        return responseData({ repopulateFields: validation.data }, response);
      } catch (e) {
        if (e instanceof Error) {
          console.log(e.stack || e);

          return validationError(e.message, validation.submittedData);
        }
        throw e;
      }
    },
  });
};

export default function Register() {
  const actionData = useActionData<typeof action>();
  const nav = useNavigation();
  const url = useURL();

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-4">
        <H1 className="mt-6 text-left text-2xl font-semibold tracking-tight text-gray-900">
          Create an Account
        </H1>

        <Form
          method="post"
          className="flex flex-col"
          validator={registerValidator}
          defaultValues={actionData?.repopulateFields}
          data-testid="register-form"
        >
          <input type="hidden" name="intent" value="registerUser" />
          <GlobalErrors data={actionData} />
          <InputField
            name="email"
            label="Email"
            autoComplete="username"
            data-testid="email-register"
          />
          <InputField
            name="firstName"
            label="First Name"
            autoComplete="given-name"
            data-testid="first-name-register"
          />
          <InputField
            name="lastName"
            label="Last Name"
            autoComplete="family-name"
            data-testid="last-name-register"
          />
          <PasswordField
            name="password"
            type="password"
            label="Password"
            autoComplete="new-password"
            data-testid="password-register"
          />
          <Label className="mb-5 text-sm font-light">
            Must contain at least 8 characters including a minimum of 1 capital
            letter, 1 number and 1 special character
          </Label>
          <CheckboxField
            name="subscribeMail"
            label="Email me with news and offers"
          />
          <FormErrors />
          <TriButton
            type="submit"
            className="align-center mb-3 mt-5 flex w-full flex-row items-center px-6 py-2 text-base font-semibold"
            value="Register"
            disabled={nav.state !== "idle"}
            isLoading={nav.state !== "idle"}
          >
            <PersonIcon className="inline-block align-baseline" /> Create My
            Account
          </TriButton>
          <Link
            to={url("/login")}
            className="inline-block w-full text-center text-base font-light text-accentalpha-11"
          >
            Already Have an Account? Sign In
          </Link>
        </Form>
      </div>
    </div>
  );
}
